import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import AppContext from "../../utils/app-context";
import { AuthContext } from "../../utils/auth-provider";
import { products, checkout } from "../../utils/stripe";
import theme from "../../styles/theme";
import { loadProjectCreditsData, refreshProjectCreditsData } from "../../helpers/index";

const tabStyles = {
  minWidth: "auto",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 12px",
};

export default function ProjectCreditsDialog({ open, onClose, storyRef, portalId }) {
  const { state, dispatch } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);
  const [amount, setAmount] = useState("");
  const [projectCredits, setProjectCredits] = useState(0);
  const [fundedHistory, setFundedHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [initialized, setInitialized] = useState(false);

  const userCredits = state.userData?.credits || 0;
  const storyTitle = state.stories.activeStory?.title || "Untitled";

  // Load credits balance and history on open
  useEffect(() => {
    if (!open || !storyRef) return;
    loadProjectCreditsData(storyRef)
      .then(({ credits, history }) => {
        setProjectCredits(credits);
        setFundedHistory(history);
        setInitialized(true);
      })
      .catch((err) => console.error("loadProjectCreditsData error:", err));
  }, [open, storyRef]);

  // Load Stripe products when Purchase tab is active
  useEffect(() => {
    if (!open || activeTab !== 2) return;
    products().then((items) => {
      if (items && items.length > 0) {
        setProductList([
          { name: "Small",  quantity: 1,  data: items[0] },
          { name: "Medium", quantity: 3,  data: items[0] },
          { name: "Large",  quantity: 10, data: items[0] },
        ]);
      }
    });
  }, [open, activeTab]);

  const handleTransferToProject = async () => {
    const num = parseInt(amount);
    if (!num || num <= 0 || num > userCredits) return;
    setLoading(true);
    dispatch({ type: "TRANSFER_CREDITS_TO_PROJECT", payload: { amount: num, storyRef } });
    setProjectCredits((prev) => prev + num);
    setAmount("");
    setLoading(false);
    setTimeout(() => doRefreshHistory(), 1000);
  };

  const handleWithdrawFromProject = async () => {
    const num = parseInt(amount);
    if (!num || num <= 0 || num > projectCredits) return;
    setLoading(true);
    dispatch({ type: "TRANSFER_CREDITS_FROM_PROJECT", payload: { amount: num, storyRef } });
    setProjectCredits((prev) => prev - num);
    setAmount("");
    setLoading(false);
    setTimeout(() => doRefreshHistory(), 1000);
  };

  const handlePurchase = (product) => {
    setPurchaseLoading(true);
    checkout(currentUser, product.data, product.quantity, portalId).then((url) => {
      window.location.assign(url);
    });
  };

  const doRefreshHistory = () => {
    if (!storyRef) return;
    refreshProjectCreditsData(storyRef)
      .then(({ credits, history }) => {
        setProjectCredits(credits);
        setFundedHistory(history);
      })
      .catch((err) => console.error("refreshProjectCreditsData error:", err));
  };

  const formatPrice = (num) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      (num / 100).toFixed(2)
    );

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  const amountNum = parseInt(amount) || 0;
  const isTransferValid = amountNum > 0 && amountNum <= userCredits;
  const isWithdrawValid = amountNum > 0 && amountNum <= projectCredits;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="project-credits-dialog-title"
    >
      <DialogTitle
        id="project-credits-dialog-title"
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Fund Project
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(e, v) => { setActiveTab(v); setAmount(""); }}
          sx={{ marginBottom: "16px", minHeight: "36px" }}
          variant="fullWidth"
        >
          <Tab label="Fund Project" sx={tabStyles} />
          <Tab label="Withdraw" sx={tabStyles} />
          <Tab label="Purchase" sx={tabStyles} />
        </Tabs>

        {/* Transfer To Project */}
        {activeTab === 0 && (
          <Box>
            <Typography variant="body2" sx={{ marginBottom: "12px" }}>
              Transfer credits from your account to "{storyTitle}".
            </Typography>
            <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <TextField
                type="number"
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
                inputProps={{ min: 1, max: userCredits }}
                sx={{ flex: 1 }}
                helperText={`Available: ${userCredits}`}
              />
              <Button
                variant="contained"
                onClick={handleTransferToProject}
                disabled={!isTransferValid || loading}
                sx={{ height: "40px" }}
              >
                {loading ? <CircularProgress size={20} /> : "Transfer"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Withdraw From Project */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" sx={{ marginBottom: "12px" }}>
              Withdraw credits from "{storyTitle}" back to your account.
            </Typography>
            <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <TextField
                type="number"
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
                inputProps={{ min: 1, max: projectCredits }}
                sx={{ flex: 1 }}
                helperText={`Project balance: ${projectCredits}`}
              />
              <Button
                variant="contained"
                onClick={handleWithdrawFromProject}
                disabled={!isWithdrawValid || loading}
                sx={{ height: "40px" }}
              >
                {loading ? <CircularProgress size={20} /> : "Withdraw"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Purchase For Project */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="body2" sx={{ marginBottom: "12px" }}>
              Purchase credits directly for "{storyTitle}".
            </Typography>
            <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {productList.map((product, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "1 1 140px",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="h6" sx={{ margin: "8px 0" }}>
                    {product.quantity * product.data.metadata.credits} credits
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: "12px", opacity: 0.7 }}>
                    {formatPrice(product.data.prices[0].unit_amount * product.quantity)}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handlePurchase(product)}
                    disabled={purchaseLoading}
                  >
                    {purchaseLoading ? "Loading..." : "Buy"}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Funding History */}
        {fundedHistory.length > 0 && (
          <Box sx={{ marginTop: "24px" }}>
            <Typography variant="subtitle2" sx={{ marginBottom: "8px", fontWeight: 600 }}>
              Funding History
            </Typography>
            <TableContainer
              sx={{
                maxHeight: "200px",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "4px",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fundedHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell sx={{ fontSize: "12px" }}>{formatDate(entry.date)}</TableCell>
                      <TableCell sx={{ fontSize: "12px", textTransform: "capitalize" }}>
                        {entry.source}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "12px", fontWeight: 600 }}>
                        {entry.amount > 0 ? "+" : ""}{entry.amount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
